
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { fixAllPollVoteCounts, reconcilePollVotes } from '@/utils/polls/pollReconciliation';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const PollReconciliationTool = () => {
  const [specificPollId, setSpecificPollId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleReconcileSpecificPoll = async () => {
    if (!specificPollId.trim()) {
      toast.error("Please enter a valid poll ID");
      return;
    }

    setIsProcessing(true);
    try {
      await reconcilePollVotes(specificPollId);
      toast.success(`Successfully reconciled vote counts for poll ID: ${specificPollId}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while reconciling poll vote counts');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReconcileAllPolls = async () => {
    setIsProcessing(true);
    try {
      await fixAllPollVoteCounts();
      toast.success('Successfully reconciled all poll vote counts');
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while reconciling poll vote counts');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="shadow-md">
      <CardHeader>
        <CardTitle>Poll Vote Count Reconciliation Tool</CardTitle>
        <CardDescription>
          Use this tool to fix discrepancies between displayed poll vote counts and actual database records
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-2">Reconcile All Polls</h3>
          <p className="text-sm text-muted-foreground mb-2">
            This will recalculate vote counts for all polls in the database based on actual vote records.
          </p>
          <Button 
            onClick={handleReconcileAllPolls} 
            disabled={isProcessing}
          >
            {isProcessing ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
            ) : 'Reconcile All Polls'}
          </Button>
        </div>
        
        <div className="pt-4 border-t">
          <h3 className="text-sm font-medium mb-2">Reconcile Specific Poll</h3>
          <p className="text-sm text-muted-foreground mb-2">
            Enter a poll ID to reconcile vote counts for a specific poll.
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="Enter Poll ID"
              value={specificPollId}
              onChange={(e) => setSpecificPollId(e.target.value)}
              className="max-w-md"
            />
            <Button 
              onClick={handleReconcileSpecificPoll} 
              disabled={isProcessing || !specificPollId.trim()}
            >
              {isProcessing ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
              ) : 'Reconcile'}
            </Button>
          </div>
        </div>
      </CardContent>
      <CardFooter className="bg-muted/50 text-xs text-muted-foreground">
        <p>
          Note: This process ensures that displayed vote counts match the actual votes in the database.
          After reconciliation, refresh the page to see updated results.
        </p>
      </CardFooter>
    </Card>
  );
};

export default PollReconciliationTool;
